import { configureStore } from '@reduxjs/toolkit';

import { appStateSlice } from './appState/appState';
import { blueprintLubavApi } from './blueprint/blueprint.lubav.api';
import { bookmarksApi } from './bookmarks/bookmarks.api';
import { careerPathSlice } from './careerPath/careerPath';
import { careerPathApi } from './careerPath/careerPath.api';
import { careerPathLubavApi } from './careerPath/careerPath.lubav.api';
import { careerPathSearchReducer } from './careerPath/careerPathSearch';
import { coachesApi } from './coaches/coaches.api';
import { coachingAnalyticsApi } from './coachingAnalytics/coachingAnalytics.api';
import { coachingDashboardApi } from './coachingDashboard/coachingDashboard.api';
import { memberProfileSummaryReducer } from './coachingDashboard/memberProfileSummary';
import { memberSolutionSessionReducer } from './coachingDashboard/memberSolutionSession';
import { officeHoursApi } from './coachLubav/coachLubav.api';
import { coachNoteEditorSlice } from './coachNoteEditor/coachNoteEditor';
import { collegeSlice } from './college/college';
import { collegeApi } from './college/college.api';
import { collegeTypesApi } from './collegeTypes/collegeTypes.api';
import { contentCategorySlice } from './contentCategory/contentCategory';
import { contentCompetencySlice } from './contentCompetency/contentCompetency';
import { contentCompetencyApi } from './contentCompetency/contentCompetency.api';
import { contentNuggetSlice } from './contentNugget/contentNugget';
import { contentNuggetApi } from './contentNugget/contentNugget.api';
import { contentSearchApi } from './contentSearch/contentSearch.api';
import { dashboardFeaturedApi } from './dashboardFeatured/dashboardFeatured.api';
import { dataDictionarySlice } from './dataDictionary/dataDictionary';
import { deepDiveApi } from './deepDive/deepDive.api';
import { educationalInvestmentApi } from './educationalInvestment/educationalInvestment.api';
import { employerSlice } from './employer/employer';
import { employerApi } from './employer/employer.api';
import { emulatorSlice } from './emulator/emulator';
import { familyApi } from './family/family.api';
import { featureFlagSlice } from './featureFlags/featureFlags';
import { generatedContentApi } from './generatedContent/generatedContent.api';
import { industryApi } from './industry/industry.api';
import { institutionApi } from './institution/institution.api';
import { institutionAdminApi } from './institutionAdmin';
import { internshipApi } from './internship/internship.api';
import { lifeReadinessApi } from './lifeReadiness/lifeReadiness.api';
import { sessionStateReducer } from './lubavSessionState/lubavSessionState';
import { majorApi } from './major/major.api';
import { majorsGuideApi } from './majorsGuide/majorsGuide.api';
import { onboardingApi, onboardingPublicApi } from './onboarding/onboarding.api';
import { oneTimeModalApi } from './oneTimeModal/oneTimeModal.api';
import { operatorContextSlice } from './operatorContext/operatorContext';
import { pageContextDescriptionsApi } from './pageContextDescriptions/pageContextDescriptions.api';
import { pagePromptsSlice } from './pagePrompts/pagePrompts';
import { prioritiesApi } from './priorities/priorities.api';
import { prioritiesSlice } from './priorities/priorities.slice';
import { productsApi } from './products/products.api';
import { profileApi } from './profile/profile';
import { satActBoosterApi } from './satActBooster/satActBooster.api';
import { satActBoosterSlice } from './satActBooster/satActBooster.slice';
import { scholarshipApi } from './scholarship/scholarship.api';
import { searchSlice } from './search/search';
import { sessionStatusesReducer } from './sessionStatuses/sessionStatuses';
import { sharedAccountSessionSlice } from './sharedSession/sharedAccountSession';
import { sharedSessionSlice } from './sharedSession/sharedSession';
import { sharedSessionApi } from './sharedSession/sharedSession.api';
import { sharedSessionGrantsApi } from './sharedSession/sharedSessionGrants.api';
import { solutionSessionApi } from './solutionSession/solutionSession.api';
import { stickyNotesApi } from './stickyNotes/stickyNotes.api';
import { stickyNotesSlice } from './stickyNotes/stickyNotes.slice';
import { subscriptionApi } from './subscription/subscriptionApi';
import { superCoachApi } from './superCoach/superCoach.api';
import { surveyStateSlice } from './survey/survey';
import { tabsSlice } from './tabs/tabs.slice';
import { toolsApi } from './tools/tools.api';
import { trackAssessmentApi } from './trackAssessment/trackAssessment.api';
import { trackAssessmentApiLubav } from './trackAssessment/trackAssessment.lubav.api';
import { userSlice } from './user/user';
import { userApi } from './user/user.api';
import { userDocumentsApi, userLubavApi } from './user/user.lubav.api';
import { compareDraftSlice } from './userColleges/compareDraft.slice';
import { userCollegesApi } from './userColleges/userColleges.api';
import { userAcademicHistoryApi } from './userPortfolio/userAcademicHistory.api';
import { userEmploymentHistoryApi } from './userPortfolio/userEmploymentHistory.api';
import { userEngagementHistoryApi } from './userPortfolio/userEngagementHistory.api';
import { userResponseSetSlice } from './userResponse/userResponse';

export const store = configureStore({
  reducer: {
    user: userSlice.reducer,
    [userApi.reducerPath]: userApi.reducer,
    [userCollegesApi.reducerPath]: userCollegesApi.reducer,
    [onboardingApi.reducerPath]: onboardingApi.reducer,
    [onboardingPublicApi.reducerPath]: onboardingPublicApi.reducer,
    [lifeReadinessApi.reducerPath]: lifeReadinessApi.reducer,
    [profileApi.reducerPath]: profileApi.reducer,
    [generatedContentApi.reducerPath]: generatedContentApi.reducer,
    [toolsApi.reducerPath]: toolsApi.reducer,
    [employerApi.reducerPath]: employerApi.reducer,
    [collegeApi.reducerPath]: collegeApi.reducer,
    [trackAssessmentApi.reducerPath]: trackAssessmentApi.reducer,
    [trackAssessmentApiLubav.reducerPath]: trackAssessmentApiLubav.reducer,
    [educationalInvestmentApi.reducerPath]: educationalInvestmentApi.reducer,
    [userDocumentsApi.reducerPath]: userDocumentsApi.reducer,
    [userLubavApi.reducerPath]: userLubavApi.reducer,
    [prioritiesApi.reducerPath]: prioritiesApi.reducer,
    [internshipApi.reducerPath]: internshipApi.reducer,
    [scholarshipApi.reducerPath]: scholarshipApi.reducer,
    [contentCompetencyApi.reducerPath]: contentCompetencyApi.reducer,
    [contentNuggetApi.reducerPath]: contentNuggetApi.reducer,
    [contentSearchApi.reducerPath]: contentSearchApi.reducer,
    [officeHoursApi.reducerPath]: officeHoursApi.reducer,
    [solutionSessionApi.reducerPath]: solutionSessionApi.reducer,
    [superCoachApi.reducerPath]: superCoachApi.reducer,
    [userAcademicHistoryApi.reducerPath]: userAcademicHistoryApi.reducer,
    [userEmploymentHistoryApi.reducerPath]: userEmploymentHistoryApi.reducer,
    [userEngagementHistoryApi.reducerPath]: userEngagementHistoryApi.reducer,
    [institutionApi.reducerPath]: institutionApi.reducer,
    [institutionAdminApi.reducerPath]: institutionAdminApi.reducer,
    [industryApi.reducerPath]: industryApi.reducer,
    [majorApi.reducerPath]: majorApi.reducer,
    [subscriptionApi.reducerPath]: subscriptionApi.reducer,
    [bookmarksApi.reducerPath]: bookmarksApi.reducer,
    [sharedSessionApi.reducerPath]: sharedSessionApi.reducer,
    [sharedSessionGrantsApi.reducerPath]: sharedSessionGrantsApi.reducer,
    [familyApi.reducerPath]: familyApi.reducer,
    [oneTimeModalApi.reducerPath]: oneTimeModalApi.reducer,
    [dashboardFeaturedApi.reducerPath]: dashboardFeaturedApi.reducer,
    [deepDiveApi.reducerPath]: deepDiveApi.reducer,
    [productsApi.reducerPath]: productsApi.reducer,
    [coachesApi.reducerPath]: coachesApi.reducer,
    [coachingDashboardApi.reducerPath]: coachingDashboardApi.reducer,
    [coachingAnalyticsApi.reducerPath]: coachingAnalyticsApi.reducer,
    [pageContextDescriptionsApi.reducerPath]: pageContextDescriptionsApi.reducer,

    // TEST BOOSTER
    [satActBoosterApi.reducerPath]: satActBoosterApi.reducer,
    satActBooster: satActBoosterSlice.reducer,

    // STICKY NOTES
    [stickyNotesApi.reducerPath]: stickyNotesApi.reducer,
    stickyNotes: stickyNotesSlice.reducer,

    // COACH NOTES
    coachNoteEditor: coachNoteEditorSlice.reducer,

    // COACHING DASHBOARD
    memberSolutionSession: memberSolutionSessionReducer,
    memberProfileSummary: memberProfileSummaryReducer,

    // PRIORITIES
    priorities: prioritiesSlice.reducer,

    // COLLEGE
    college: collegeSlice.reducer,
    compareDraft: compareDraftSlice.reducer,

    // EMPLOYER
    employer: employerSlice.reducer,

    // PAGE PROMPTS
    pagePrompts: pagePromptsSlice.reducer,

    // BLUEPRINT
    userResponseSet: userResponseSetSlice.reducer,
    survey: surveyStateSlice.reducer,
    careerPath: careerPathSlice.reducer,
    dataDictionary: dataDictionarySlice.reducer,

    // PROSPER
    contentCategory: contentCategorySlice.reducer,
    contentCompetency: contentCompetencySlice.reducer,
    contentNugget: contentNuggetSlice.reducer,
    search: searchSlice.reducer,

    // LUBAV SESSIONS
    sessionState: sessionStateReducer,
    sessionStatuses: sessionStatusesReducer,
    sharedSession: sharedSessionSlice.reducer,
    sharedAccountSession: sharedAccountSessionSlice.reducer,

    // OPERATOR CONTEXT
    operatorContext: operatorContextSlice.reducer,

    // APP STATE
    appState: appStateSlice.reducer,
    tabs: tabsSlice.reducer,
    emulator: emulatorSlice.reducer,

    // CAREER PATH SEARCH
    careerPathSearch: careerPathSearchReducer,
    [careerPathApi.reducerPath]: careerPathApi.reducer,
    [careerPathLubavApi.reducerPath]: careerPathLubavApi.reducer,
    [blueprintLubavApi.reducerPath]: blueprintLubavApi.reducer,

    // FEATURE FLAGS
    featureFlags: featureFlagSlice.reducer,

    // MAJORS GUIDE
    [majorsGuideApi.reducerPath]: majorsGuideApi.reducer,

    // COLLEGE TYPES
    [collegeTypesApi.reducerPath]: collegeTypesApi.reducer,
  },

  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      userApi.middleware,
      userCollegesApi.middleware,
      onboardingApi.middleware,
      onboardingPublicApi.middleware,
      lifeReadinessApi.middleware,
      profileApi.middleware,
      generatedContentApi.middleware,
      toolsApi.middleware,
      employerApi.middleware,
      collegeApi.middleware,
      trackAssessmentApi.middleware,
      trackAssessmentApiLubav.middleware,
      educationalInvestmentApi.middleware,
      prioritiesApi.middleware,
      userDocumentsApi.middleware,
      userLubavApi.middleware,
      careerPathApi.middleware,
      careerPathLubavApi.middleware,
      blueprintLubavApi.middleware,
      internshipApi.middleware,
      scholarshipApi.middleware,
      contentCompetencyApi.middleware,
      contentNuggetApi.middleware,
      contentSearchApi.middleware,
      officeHoursApi.middleware,
      solutionSessionApi.middleware,
      superCoachApi.middleware,
      institutionApi.middleware,
      industryApi.middleware,
      majorApi.middleware,
      subscriptionApi.middleware,
      userAcademicHistoryApi.middleware,
      userEmploymentHistoryApi.middleware,
      userEngagementHistoryApi.middleware,
      bookmarksApi.middleware,
      sharedSessionApi.middleware,
      sharedSessionGrantsApi.middleware,
      familyApi.middleware,
      oneTimeModalApi.middleware,
      majorsGuideApi.middleware,
      collegeTypesApi.middleware,
      dashboardFeaturedApi.middleware,
      deepDiveApi.middleware,
      productsApi.middleware,
      coachesApi.middleware,
      coachingDashboardApi.middleware,
      institutionAdminApi.middleware,
      coachingAnalyticsApi.middleware,
      pageContextDescriptionsApi.middleware,
      satActBoosterApi.middleware,
      stickyNotesApi.middleware,
      // additionally, should it be required please add it to the logoutThunk
    ),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
